import i18n from '@/utils/i18n';
import React, { useState } from 'react';
import { ActionSheet } from '@/components/action-sheet';
import { AutoPayNoPaymentMethodsContainer } from '@/components/payments/autopay-no-payment-methods-container';
import { AutoPaySettingsContainerMobile } from '@/components/payments/autopay-settings-container-mobile';
import { CompositeNavigationProp } from '@react-navigation/native';
import { PageContainer } from '@/components/page-container';
import { PAYMENT_ROUTES } from '@/navigators/routes';
import { PaymentsStackParams } from '@/navigators/types';
import { StackNavigationProp } from '@react-navigation/stack';
import { StyleSheet } from 'react-native';
import { useAutopaySettings } from '@/hooks/payments/use-autopay-settings';
import { usePaymentsContext } from '@/contexts/payments-context';
import { useToasts } from '@/contexts/toast-context';

interface Props {
  navigation: CompositeNavigationProp<
    StackNavigationProp<PaymentsStackParams, PAYMENT_ROUTES.AutoPay>,
    CompositeNavigationProp<
      StackNavigationProp<PaymentsStackParams, PAYMENT_ROUTES.AutoPay>,
      StackNavigationProp<PaymentsStackParams>
    >
  >;
}

export const AutoPayScreen: React.FC<Props> = ({ navigation }) => {
  const { showErrorToast } = useToasts();
  const {
    autoPaySelfServeEnabled,
    currencyCode,
    savingAutoPaySetting,
    selectAutoPaySettings,
    selectedPaymentMethod,
  } = usePaymentsContext();

  const {
    availableAutoPaySettings,
    hasSavedAutoPaySetting,
    selectedAutoPaySettingUUID,
    setSelectedAutoPaySettingUUID,
    shouldSaveAutoPaySetting,
  } = useAutopaySettings();

  const [showSaveChanges, setShowSaveChanges] = useState(false);

  const onAddPaymentMethod = () => {
    navigation.push(PAYMENT_ROUTES.PaymentMethod, {
      onSavePaymentMethod: () => {
        navigation.navigate(PAYMENT_ROUTES.AutoPay);
      },
      requireSavedPaymentMethod: true,
    });
  };
  const onChangePaymentMethod = () => {
    navigation.push(PAYMENT_ROUTES.PaymentMethod, {
      onSavePaymentMethod: () => {
        navigation.navigate(PAYMENT_ROUTES.AutoPay);
      },
      requireSavedPaymentMethod: false,
    });
  };

  const onSaveAutoPaySetting = (autoPaySettingUuid: string) => {
    try {
      selectAutoPaySettings(autoPaySettingUuid);
      setSelectedAutoPaySettingUUID(autoPaySettingUuid);
    } catch (e) {
      showErrorToast();
    }
  };

  const onUnsavedChangesAction = async (_: unknown, index: number) => {
    if (index === 0) {
      if (selectedAutoPaySettingUUID) {
        onSaveAutoPaySetting(selectedAutoPaySettingUUID);
      }
      setShowSaveChanges(false);
      navigation.goBack();
    } else {
      setShowSaveChanges(false);
      navigation.goBack();
    }
  };

  return (
    <PageContainer
      navigationHeaderProps={{
        centerTitle: true,
        onBackPress: () => {
          if (shouldSaveAutoPaySetting) {
            setShowSaveChanges(true);
          } else {
            navigation.goBack();
          }
        },
        showBackButton: true,
        showShadow: false,
        title: i18n.t('autopay.title'),
      }}
    >
      {!selectedPaymentMethod ? (
        <AutoPayNoPaymentMethodsContainer
          onAddPaymentMethod={onAddPaymentMethod}
        />
      ) : (
        <AutoPaySettingsContainerMobile
          autoPaySelfServeEnabled={autoPaySelfServeEnabled}
          availableAutoPaySettings={availableAutoPaySettings}
          containerStyle={styles.container}
          currencyCode={currencyCode}
          hasSavedAutoPaySetting={hasSavedAutoPaySetting}
          onChangePaymentMethod={onChangePaymentMethod}
          onSaveAutoPaySetting={onSaveAutoPaySetting}
          onSelectAutoPaySetting={setSelectedAutoPaySettingUUID}
          savingAutoPaySetting={savingAutoPaySetting}
          selectedAutoPaySettingUUID={selectedAutoPaySettingUUID}
          selectedPaymentMethod={selectedPaymentMethod}
          shouldSaveAutoPaySetting={shouldSaveAutoPaySetting}
        />
      )}
      <ActionSheet
        actions={[i18n.t('save'), i18n.t('discard_changes')]}
        isVisible={showSaveChanges}
        message={i18n.t('discard_changes_disclaimer')}
        onAction={onUnsavedChangesAction}
        onClose={() => setShowSaveChanges(false)}
        title={i18n.t('action_sheet.save.title')}
      />
    </PageContainer>
  );
};

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 16,
    paddingTop: 8,
  },
});
